import type { AdultProfile, ChildProfile } from '@yoyoclub/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../ui/Button';
import Card from '../ui/Card';

interface ProfileSelectorProps {
  type: 'child' | 'adult';
  profiles: ChildProfile[] | AdultProfile[];
  selectedIds: string[];
  onSelect: (profileIds: string[]) => void;
  onClose: () => void;
  maxSelectable?: number; // Max new profiles that can be selected (undefined = unlimited)
}

/**
 * Modal for selecting profiles from saved child/adult profiles
 * Supports multi-select and shows which profiles are already selected
 */
export default function ProfileSelector({
  type,
  profiles,
  selectedIds,
  onSelect,
  onClose,
  maxSelectable,
}: ProfileSelectorProps) {
  const { t } = useTranslation('booking');
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedIds);
  const typeLabel =
    type === 'child'
      ? t('components.profileSelector.child')
      : t('components.profileSelector.adult');

  const newlySelectedCount = tempSelectedIds.filter((id) => !selectedIds.includes(id)).length;
  const isAtMax = maxSelectable !== undefined && newlySelectedCount >= maxSelectable;

  const handleToggleProfile = (profileId: string) => {
    if (tempSelectedIds.includes(profileId)) {
      setTempSelectedIds(tempSelectedIds.filter((id) => id !== profileId));
    } else if (!isAtMax || selectedIds.includes(profileId)) {
      setTempSelectedIds([...tempSelectedIds, profileId]);
    }
  };

  const handleConfirm = () => {
    // Only pass newly selected profiles (not already in selectedIds)
    const newlySelected = tempSelectedIds.filter((id) => !selectedIds.includes(id));
    onSelect(newlySelected);
  };

  // Calculate age for child profiles
  const getAge = (birthDate: Date): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {type === 'child'
                ? t('components.profileSelector.selectChild')
                : t('components.profileSelector.selectAdult')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="space-y-3">
            {profiles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('components.profileSelector.noProfiles', { type: typeLabel })}
              </div>
            ) : (
              profiles.map((profile) => {
                const isSelected = tempSelectedIds.includes(profile.id);
                const isAlreadySelected = selectedIds.includes(profile.id);
                const isDisabled = isAlreadySelected || (isAtMax && !isSelected);

                return (
                  <div
                    key={profile.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => !isDisabled && handleToggleProfile(profile.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => !isDisabled && handleToggleProfile(profile.id)}
                          disabled={isDisabled}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div className="flex-1">
                        {type === 'child' ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {(profile as ChildProfile).preferredName ||
                                (profile as ChildProfile).name}
                            </div>
                            <div className="text-sm text-gray-600">
                              {t('components.profileSelector.age')}{' '}
                              {getAge((profile as ChildProfile).birthDate)}
                            </div>
                            {(profile as ChildProfile).notes && (
                              <div className="text-xs text-gray-500 mt-1">
                                {t('components.profileSelector.note')}:{' '}
                                {(profile as ChildProfile).notes}
                              </div>
                            )}
                            {isAlreadySelected && (
                              <div className="text-xs text-primary mt-1">
                                {t('components.profileSelector.alreadySelected')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium text-gray-900">
                              {(profile as AdultProfile).name}
                            </div>
                            <div className="text-sm text-gray-600">
                              {(profile as AdultProfile).phone}
                            </div>
                            {isAlreadySelected && (
                              <div className="text-xs text-primary mt-1">
                                {t('components.profileSelector.alreadySelected')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={onClose} fullWidth>
              {t('components.profileSelector.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              fullWidth
              disabled={tempSelectedIds.filter((id) => !selectedIds.includes(id)).length === 0}
            >
              {t('components.profileSelector.addSelected', {
                count: tempSelectedIds.filter((id) => !selectedIds.includes(id)).length,
              })}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
